import { useQueries } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Granularity, Grouping } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { Usage } from '../../../telemetry/gen/ai/h2o/cloud/telemetry_read_api/v1/aiunits_pb';
import { useTelemetryService } from '../../../telemetry/hooks';
import { usePlatformUsageConfig } from '../config';
import { UsageDataResult } from '../types';

/**
 * Interface for a query combination (time range + grouping)
 */
interface QueryCombination {
  key: string;
  startTime: Date;
  endTime: Date;
  granularity: Granularity;
  groupBy: Grouping;
  stage: number; // Stage number for progressive loading
}

/**
 * Check if two dates are equal (ignoring milliseconds)
 */
function datesAreEqual(date1: Date, date2: Date): boolean {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate() &&
    date1.getHours() === date2.getHours() &&
    date1.getMinutes() === date2.getMinutes() &&
    date1.getSeconds() === date2.getSeconds()
  );
}

/**
 * Check if a query combination matches the current selection
 */
function matchesCurrentSelection(
  combo: QueryCombination,
  currentTimeRange: { startTime: Date; endTime: Date; granularity: Granularity },
  currentGroupBy: Grouping
): boolean {
  return (
    datesAreEqual(combo.startTime, currentTimeRange.startTime) &&
    datesAreEqual(combo.endTime, currentTimeRange.endTime) &&
    combo.granularity === currentTimeRange.granularity &&
    combo.groupBy === currentGroupBy
  );
}

/**
 * Custom hook that prefetches usage data in stages to reduce server load
 *
 * Loading strategy is configurable via platform-usage-config.yaml:
 * - Stage 1: First preset + immediate groupings (for filter options)
 * - Stage 2: First preset + delayed groupings (after stage2DelayMs)
 * - Stages 3+: Other presets with immediate groupings first, then delayed
 *
 * For custom date ranges (not matching any preset):
 * - Immediate groupings fetched right away (for filter options)
 * - Delayed groupings fetched after customRangeDelayMs
 *
 * @param selectedServices Array of service filter strings
 * @param selectedWorkspaces Array of workspace filter strings (UUIDs)
 * @param selectedUsers Array of user filter strings (UUIDs)
 * @param selectedTags Array of tag filter strings
 * @param currentTimeRange Currently selected time range and granularity
 * @param currentGroupBy Currently selected grouping option
 * @returns Object containing usage data for current selection, loading state, error, and refetch function
 */
export function useMultiDimensionalPrefetch(
  selectedServices: string[],
  selectedWorkspaces: string[],
  selectedUsers: string[],
  selectedTags: string[],
  currentTimeRange: { startTime: Date; endTime: Date; granularity: Granularity },
  currentGroupBy: Grouping
): UsageDataResult {
  const TelemetryService = useTelemetryService();
  const config = usePlatformUsageConfig();
  const { timing, groupingStrategy, timeRangePresets } = config;

  const [completedStages, setCompletedStages] = useState<Set<number>>(new Set());
  const [stage2TimerFired, setStage2TimerFired] = useState(false);

  // Start timer to enable Stage 2 queries
  // This allows the default workspace query to start first, then prefetch others
  useEffect(() => {
    const timer = setTimeout(() => {
      setStage2TimerFired(true);
    }, timing.stage2DelayMs);
    return () => clearTimeout(timer);
  }, [timing.stage2DelayMs]);

  // Track custom range for prefetch timer
  const [customRangePrefetchEnabled, setCustomRangePrefetchEnabled] = useState(false);
  const customRangeTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastCustomRangeKeyRef = useRef<string>('');

  // Generate a stable key for the current time range
  const customRangeKey = `${currentTimeRange.startTime.toISOString()}-${currentTimeRange.endTime.toISOString()}-${
    currentTimeRange.granularity
  }-${selectedServices.length > 0 ? [...selectedServices].sort().join(',') : 'none'}-${
    selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none'
  }-${selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none'}-${
    selectedTags.length > 0 ? [...selectedTags].sort().join(',') : 'none'
  }`;

  // Check if current selection matches any preset
  const isCustomRange = !timeRangePresets.some(
    (preset) =>
      datesAreEqual(preset.startTime, currentTimeRange.startTime) &&
      datesAreEqual(preset.endTime, currentTimeRange.endTime) &&
      preset.granularity === currentTimeRange.granularity
  );

  // Start timer when custom range changes
  useEffect(() => {
    if (isCustomRange && customRangeKey !== lastCustomRangeKeyRef.current) {
      // Clear existing timer
      if (customRangeTimerRef.current) {
        clearTimeout(customRangeTimerRef.current);
      }
      // Reset prefetch state for new custom range
      setCustomRangePrefetchEnabled(false);
      lastCustomRangeKeyRef.current = customRangeKey;

      // Start new timer
      customRangeTimerRef.current = setTimeout(() => {
        setCustomRangePrefetchEnabled(true);
      }, timing.customRangeDelayMs);
    }

    return () => {
      if (customRangeTimerRef.current) {
        clearTimeout(customRangeTimerRef.current);
      }
    };
  }, [isCustomRange, customRangeKey, timing.customRangeDelayMs]);

  // Helper to check if a grouping is in the immediate groupings list
  const isImmediateGrouping = (groupBy: Grouping): boolean => {
    return groupingStrategy.immediateGroupings.includes(groupBy);
  };

  // Fallback queries for custom date ranges - fetches all groupings
  // Immediate groupings right away, delayed groupings after timer
  const customRangeQueries = useQueries({
    queries: groupingStrategy.allGroupings.map((groupBy) => {
      const isImmediate = isImmediateGrouping(groupBy);

      return {
        queryKey: [
          'usageData',
          {
            startTime: currentTimeRange.startTime.toISOString(),
            endTime: currentTimeRange.endTime.toISOString(),
            granularity: currentTimeRange.granularity,
            groupBy,
            servicesKey: selectedServices.length > 0 ? [...selectedServices].sort().join(',') : 'none',
            workspacesKey: selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none',
            usersKey: selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none',
            tagsKey: selectedTags.length > 0 ? [...selectedTags].sort().join(',') : 'none',
          },
        ],
        queryFn: async (): Promise<Usage[]> => {
          const startTimeISO = currentTimeRange.startTime.toISOString();
          const endTimeISO = currentTimeRange.endTime.toISOString();

          // Build combined filter string from all filter types
          let filterString: string | undefined = undefined;
          const filterParts: string[] = [];

          // Service filter
          if (selectedServices.length > 0) {
            const serviceFilter = selectedServices.map((svc) => `service = '${svc}'`).join(' OR ');
            filterParts.push(serviceFilter);
          }

          // Workspace filter
          if (selectedWorkspaces.length > 0) {
            const workspaceFilter = selectedWorkspaces.map((ws) => `workspace = '${ws}'`).join(' OR ');
            filterParts.push(workspaceFilter);
          }

          // User filter
          if (selectedUsers.length > 0) {
            const userFilter = selectedUsers.map((user) => `user = '${user}'`).join(' OR ');
            filterParts.push(userFilter);
          }

          // Tag filter
          if (selectedTags.length > 0) {
            const tagFilter = selectedTags.map((tag) => `tag = '${tag}'`).join(' OR ');
            filterParts.push(tagFilter);
          }

          // Combine all filter parts with AND (wrap OR expressions in parens when combining)
          if (filterParts.length > 0) {
            if (filterParts.length === 1) {
              filterString = filterParts[0];
            } else {
              filterString = filterParts.map((part) => (part.includes(' OR ') ? `(${part})` : part)).join(' AND ');
            }
          }

          const response = await TelemetryService.calculateResourceUsage({
            startTime: startTimeISO,
            endTime: endTimeISO,
            granularity: currentTimeRange.granularity,
            groupBy,
            ...(filterString && { filter: filterString }),
          });

          return response.usage || [];
        },
        // Enable immediate groupings right away, delayed groupings after timer
        enabled: isCustomRange && (isImmediate || customRangePrefetchEnabled),
        staleTime: timing.cacheDurationMs,
        cacheTime: timing.cacheDurationMs,
        retry: timing.retryAttempts,
        retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, timing.retryMaxDelayMs),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        onError: (error: Error) => {
          console.error('[useMultiDimensionalPrefetch] Custom range error:', error, { groupBy });
        },
      };
    }),
  });

  // Generate all combinations with stage assignments
  // Memoize to avoid recreating on every render
  const { allCombinations, totalStages } = useMemo(() => {
    const combinations: QueryCombination[] = [];

    // Stage 1: First preset + immediate groupings (for filter options)
    if (timeRangePresets[0]) {
      groupingStrategy.immediateGroupings.forEach((groupBy) => {
        combinations.push({
          key: `${timeRangePresets[0].key}-${groupBy}`,
          startTime: timeRangePresets[0].startTime,
          endTime: timeRangePresets[0].endTime,
          granularity: timeRangePresets[0].granularity,
          groupBy,
          stage: 1,
        });
      });
    }

    // Stage 2: First preset + delayed groupings (after stage2DelayMs)
    if (timeRangePresets[0]) {
      groupingStrategy.delayedGroupings.forEach((groupBy) => {
        combinations.push({
          key: `${timeRangePresets[0].key}-${groupBy}`,
          startTime: timeRangePresets[0].startTime,
          endTime: timeRangePresets[0].endTime,
          granularity: timeRangePresets[0].granularity,
          groupBy,
          stage: 2,
        });
      });
    }

    // Stages 3+: Other time ranges with immediate groupings first, then delayed groupings
    timeRangePresets.slice(1).forEach((preset, index) => {
      const baseStage = (index + 1) * 2 + 1; // Stages 3, 5, 7, 9, etc.

      // First stage for this time range: immediate groupings
      groupingStrategy.immediateGroupings.forEach((groupBy) => {
        combinations.push({
          key: `${preset.key}-${groupBy}`,
          startTime: preset.startTime,
          endTime: preset.endTime,
          granularity: preset.granularity,
          groupBy,
          stage: baseStage,
        });
      });

      // Second stage for this time range: delayed groupings
      groupingStrategy.delayedGroupings.forEach((groupBy) => {
        combinations.push({
          key: `${preset.key}-${groupBy}`,
          startTime: preset.startTime,
          endTime: preset.endTime,
          granularity: preset.granularity,
          groupBy,
          stage: baseStage + 1,
        });
      });
    });

    // Calculate total stages: 2 for first preset + 2 per additional preset
    const numStages = timeRangePresets.length > 0 ? 2 + (timeRangePresets.length - 1) * 2 : 0;

    return { allCombinations: combinations, totalStages: numStages };
  }, [timeRangePresets, groupingStrategy]);

  // Create queries with staged loading
  const queries = useQueries({
    queries: allCombinations.map((combo) => ({
      queryKey: [
        'usageData',
        {
          startTime: combo.startTime.toISOString(),
          endTime: combo.endTime.toISOString(),
          granularity: combo.granularity,
          groupBy: combo.groupBy,
          servicesKey: selectedServices.length > 0 ? [...selectedServices].sort().join(',') : 'none',
          workspacesKey: selectedWorkspaces.length > 0 ? [...selectedWorkspaces].sort().join(',') : 'none',
          usersKey: selectedUsers.length > 0 ? [...selectedUsers].sort().join(',') : 'none',
          tagsKey: selectedTags.length > 0 ? [...selectedTags].sort().join(',') : 'none',
        },
      ],
      queryFn: async (): Promise<Usage[]> => {
        const startTimeISO = combo.startTime.toISOString();
        const endTimeISO = combo.endTime.toISOString();

        // Build combined filter string from all filter types
        let filterString: string | undefined = undefined;
        const filterParts: string[] = [];

        // Service filter
        if (selectedServices.length > 0) {
          const serviceFilter = selectedServices.map((svc) => `service = '${svc}'`).join(' OR ');
          filterParts.push(serviceFilter);
        }

        // Workspace filter
        if (selectedWorkspaces.length > 0) {
          const workspaceFilter = selectedWorkspaces.map((ws) => `workspace = '${ws}'`).join(' OR ');
          filterParts.push(workspaceFilter);
        }

        // User filter
        if (selectedUsers.length > 0) {
          const userFilter = selectedUsers.map((user) => `user = '${user}'`).join(' OR ');
          filterParts.push(userFilter);
        }

        // Tag filter
        if (selectedTags.length > 0) {
          const tagFilter = selectedTags.map((tag) => `tag = '${tag}'`).join(' OR ');
          filterParts.push(tagFilter);
        }

        // Combine all filter parts with AND (wrap OR expressions in parens when combining)
        if (filterParts.length > 0) {
          if (filterParts.length === 1) {
            filterString = filterParts[0];
          } else {
            filterString = filterParts.map((part) => (part.includes(' OR ') ? `(${part})` : part)).join(' AND ');
          }
        }

        const response = await TelemetryService.calculateResourceUsage({
          startTime: startTimeISO,
          endTime: endTimeISO,
          granularity: combo.granularity,
          groupBy: combo.groupBy,
          ...(filterString && { filter: filterString }),
        });

        return response.usage || [];
      },
      // Enable query based on stage:
      // - Stage 1: enabled immediately
      // - Stage 2: enabled after stage2DelayMs timer
      // - Stages 3+: enabled after previous stage completes
      enabled:
        combo.stage === 1 ||
        (combo.stage === 2 && stage2TimerFired) ||
        (combo.stage > 2 && completedStages.has(combo.stage - 1)),
      staleTime: timing.cacheDurationMs,
      cacheTime: timing.cacheDurationMs,
      retry: timing.retryAttempts,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, timing.retryMaxDelayMs),
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onError: (error: Error) => {
        console.error('[useMultiDimensionalPrefetch] Error (will retry):', error, {
          stage: combo.stage,
          key: combo.key,
        });
      },
    })),
  });

  // Track stage completion to trigger next stage
  useEffect(() => {
    const stageQueries = new Map<number, typeof queries>();

    // Group queries by stage
    allCombinations.forEach((combo, index) => {
      if (!stageQueries.has(combo.stage)) {
        stageQueries.set(combo.stage, []);
      }
      stageQueries.get(combo.stage)!.push(queries[index]);
    });

    // Check which stages are complete
    const newCompletedStages = new Set(completedStages);
    let hasChanges = false;

    for (let stage = 1; stage <= totalStages; stage++) {
      if (!completedStages.has(stage)) {
        const stageQueriesForStage = stageQueries.get(stage) || [];
        const allComplete = stageQueriesForStage.every(
          (query) => query.isSuccess || query.isError // Consider failed queries as complete to prevent blocking
        );

        if (allComplete && stageQueriesForStage.length > 0) {
          newCompletedStages.add(stage);
          hasChanges = true;
        }
      }
    }

    if (hasChanges) {
      setCompletedStages(newCompletedStages);
    }
  }, [queries, allCombinations, completedStages, totalStages]);

  // Find the query that matches the current selection
  const currentQueryIndex = allCombinations.findIndex((combo) =>
    matchesCurrentSelection(combo, currentTimeRange, currentGroupBy)
  );

  // Find workspace and user queries for the current time range (for filter options)
  const workspaceQueryIndex = allCombinations.findIndex(
    (combo) =>
      datesAreEqual(combo.startTime, currentTimeRange.startTime) &&
      datesAreEqual(combo.endTime, currentTimeRange.endTime) &&
      combo.granularity === currentTimeRange.granularity &&
      combo.groupBy === Grouping.WORKSPACE
  );

  const userQueryIndex = allCombinations.findIndex(
    (combo) =>
      datesAreEqual(combo.startTime, currentTimeRange.startTime) &&
      datesAreEqual(combo.endTime, currentTimeRange.endTime) &&
      combo.granularity === currentTimeRange.granularity &&
      combo.groupBy === Grouping.USER
  );

  const tagQueryIndex = allCombinations.findIndex(
    (combo) =>
      datesAreEqual(combo.startTime, currentTimeRange.startTime) &&
      datesAreEqual(combo.endTime, currentTimeRange.endTime) &&
      combo.granularity === currentTimeRange.granularity &&
      combo.groupBy === Grouping.TAG
  );

  const serviceQueryIndex = allCombinations.findIndex(
    (combo) =>
      datesAreEqual(combo.startTime, currentTimeRange.startTime) &&
      datesAreEqual(combo.endTime, currentTimeRange.endTime) &&
      combo.granularity === currentTimeRange.granularity &&
      combo.groupBy === Grouping.SERVICE
  );

  // Refetch function for all queries
  const refetchAll = () => {
    // Reset completed stages and timers to restart the progressive loading
    setCompletedStages(new Set());
    setStage2TimerFired(false);
    setCustomRangePrefetchEnabled(false);
    // Restart timers
    setTimeout(() => setStage2TimerFired(true), timing.stage2DelayMs);
    setTimeout(() => setCustomRangePrefetchEnabled(true), timing.customRangeDelayMs);
    queries.forEach((query) => query.refetch());
    customRangeQueries.forEach((query) => query.refetch());
  };

  // If no exact match found, use custom range queries (prefetches all groupings)
  if (currentQueryIndex === -1) {
    const customQueryIndex = groupingStrategy.allGroupings.indexOf(currentGroupBy);
    const currentCustomQuery = customRangeQueries[customQueryIndex];

    // Get workspace, user, tag, and component data from custom range queries
    const workspaceCustomQueryIndex = groupingStrategy.allGroupings.indexOf(Grouping.WORKSPACE);
    const userCustomQueryIndex = groupingStrategy.allGroupings.indexOf(Grouping.USER);
    const tagCustomQueryIndex = groupingStrategy.allGroupings.indexOf(Grouping.TAG);
    const serviceCustomQueryIndex = groupingStrategy.allGroupings.indexOf(Grouping.SERVICE);

    // Refetch only the current custom query (not all queries)
    const refetchCurrent = () => {
      currentCustomQuery?.refetch();
    };

    return {
      usageData: currentCustomQuery?.data,
      workspaceData: customRangeQueries[workspaceCustomQueryIndex]?.data,
      userData: customRangeQueries[userCustomQueryIndex]?.data,
      tagData: customRangeQueries[tagCustomQueryIndex]?.data,
      serviceData: customRangeQueries[serviceCustomQueryIndex]?.data,
      isLoading: currentCustomQuery?.isLoading ?? true,
      error: currentCustomQuery?.error || undefined,
      refetch: refetchAll,
      refetchCurrent,
    };
  }

  // Matching prefetched query found
  const currentQuery = queries[currentQueryIndex];
  const currentCombo = allCombinations[currentQueryIndex];

  // Check if this query's stage hasn't been enabled yet
  const isWaitingForPreviousStage =
    (currentCombo.stage === 2 && !stage2TimerFired) ||
    (currentCombo.stage > 2 && !completedStages.has(currentCombo.stage - 1));

  // Get workspace, user, tag, and service queries
  const workspaceQuery = workspaceQueryIndex !== -1 ? queries[workspaceQueryIndex] : null;
  const userQuery = userQueryIndex !== -1 ? queries[userQueryIndex] : null;
  const tagQuery = tagQueryIndex !== -1 ? queries[tagQueryIndex] : null;
  const serviceQuery = serviceQueryIndex !== -1 ? queries[serviceQueryIndex] : null;

  // Refetch only the current prefetched query (not all queries)
  const refetchCurrent = () => {
    currentQuery?.refetch();
  };

  // If waiting, show loading state even though query is disabled
  if (isWaitingForPreviousStage) {
    return {
      usageData: undefined,
      workspaceData: workspaceQuery?.data,
      userData: userQuery?.data,
      tagData: tagQuery?.data,
      serviceData: serviceQuery?.data,
      isLoading: true, // Show loading spinner
      error: undefined,
      refetch: refetchAll,
      refetchCurrent,
    };
  }

  // Check if query is actually loading or fetching
  if (currentQuery.isLoading || currentQuery.isFetching) {
    return {
      usageData: currentQuery.data,
      workspaceData: workspaceQuery?.data,
      userData: userQuery?.data,
      tagData: tagQuery?.data,
      serviceData: serviceQuery?.data,
      isLoading: true,
      error: currentQuery.error || undefined,
      refetch: refetchAll,
      refetchCurrent,
    };
  }

  // Data is ready (success or cached)
  return {
    usageData: currentQuery.data,
    workspaceData: workspaceQuery?.data,
    userData: userQuery?.data,
    tagData: tagQuery?.data,
    serviceData: serviceQuery?.data,
    isLoading: false,
    error: currentQuery.error || undefined,
    refetch: refetchAll,
    refetchCurrent,
  };
}
